import { createContext, useContext, useMemo } from 'react';
import type { RentalApplication, TermSetStatus, UserStatus } from '../graphql/queries';
import type { PaymentRequestTreatment } from '../utils/ab-tests';
import type { FIELD_IDS } from '../constants/wizard';

// Context for when viewing a single application
export const ApplicationContext = createContext<ApplicationContextValue | undefined>(undefined);

export type FeatureFlags = {
  // We don't use optional or undefined here to indicate the the FeatureFlags objects
  // is only considered valid when the whole object has been loaded
  paymentRequestTreatment: PaymentRequestTreatment;
};

export interface ApplicationContextValue {
  application: RentalApplication & {
    answers?: {
      [FIELD_IDS.REQUEST_ZILLOW_PAYMENTS]?: boolean | null;
    } | null;
  };
  userStatuses: Array<UserStatus>;
  termSetStatus: TermSetStatus;
  featureFlags: FeatureFlags;
  isFirstTimeUser?: boolean;
}

export function ApplicationContextProvider({
  application,
  userStatuses,
  termSetStatus,
  featureFlags,
  isFirstTimeUser,
  children,
}: React.PropsWithChildren<ApplicationContextValue>) {
  const value = useMemo(
    () => ({
      application,
      userStatuses,
      featureFlags,
      termSetStatus,
      isFirstTimeUser: !!isFirstTimeUser,
    }),
    [application, userStatuses, featureFlags, isFirstTimeUser, termSetStatus],
  );
  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
}

export function useApplicationContext() {
  const contextValue = useContext(ApplicationContext);
  if (!contextValue) {
    throw new Error('Application data needs to be loaded first');
  }
  return contextValue;
}

export function useApplicationContextOrUndefined() {
  return useContext(ApplicationContext);
}

export function useFeatureFlags() {
  const { featureFlags } = useApplicationContext();
  return featureFlags;
}
